/**
 * Next.js middleware for AgentShield
 *
 * Uses edge-safe imports to avoid WASM in Edge Runtime
 */

import { NextRequest, NextResponse } from 'next/server';
// Use wrapper for EdgeAgentDetector to match expected interface
import { EdgeAgentDetectorWrapper as AgentDetector } from './edge-detector-wrapper';
// Import WASM-enhanced detector as an alternative
import { EdgeAgentDetectorWrapperWithWasm as WasmAgentDetector } from './edge-detector-with-wasm';
import { EdgeSessionTracker } from './session-tracker';
import type { NextJSMiddlewareConfig } from './types';

/**
 * Create AgentShield middleware for Next.js
 */
export function createAgentShieldMiddleware(
  config: Partial<NextJSMiddlewareConfig> = {}
) {
  // Use WASM-enhanced detector if enableWasm is true, otherwise use pattern-only detector
  const detector = config.enableWasm 
    ? new WasmAgentDetector({ enableWasm: true })
    : new AgentDetector(config);

  // Initialize session tracker if enabled (or if WASM is enabled)
  const sessionTracker =
    config.sessionTracking?.enabled || config.enableWasm
      ? new EdgeSessionTracker({
          enabled: true,
          ...config.sessionTracking,
        })
      : null;

  // Wire up event handlers if provided
  if (config.events) {
    Object.entries(config.events).forEach(([event, handler]) => {
      detector.on(event as any, handler as any);
    });
  }

  const {
    onAgentDetected = 'log',
    onDetection,
    skipPaths = [],
    blockedResponse = {
      status: 403,
      message: 'Access denied: Automated agent detected',
      headers: { 'Content-Type': 'application/json' },
    },
    redirectUrl = '/blocked',
    rewriteUrl = '/blocked',
  } = config;

  return async (request: NextRequest): Promise<NextResponse> => {
    try {
      // Check if path should be skipped
      const shouldSkip = skipPaths.some(pattern => {
        if (typeof pattern === 'string') {
          return request.nextUrl.pathname.startsWith(pattern);
        }
        return pattern.test(request.nextUrl.pathname);
      });

      if (shouldSkip) {
        // Mark as skipped in request
        (request as any).agentShield = { skipped: true };
        return NextResponse.next();
      }

      // Check for existing session first
      const existingSession = sessionTracker
        ? await sessionTracker.check(request)
        : null;

      if (existingSession) {
        // This is a continued AI agent session
        const response = NextResponse.next();

        // Set headers to indicate continued session
        response.headers.set('x-agentshield-detected', 'true');
        response.headers.set('x-agentshield-agent', existingSession.agent);
        response.headers.set(
          'x-agentshield-confidence',
          existingSession.confidence.toString()
        );
        response.headers.set('x-agentshield-session', 'continued');
        response.headers.set('x-agentshield-session-id', existingSession.id);

        // Mark request with session info
        (request as any).agentShield = {
          result: {
            isAgent: true,
            confidence: existingSession.confidence,
            detectedAgent: { name: existingSession.agent },
            timestamp: new Date(),
            verificationMethod: 'session',
          },
          session: existingSession,
          skipped: false,
        };

        // Emit session continued event
        const context = {
          userAgent: request.headers.get('user-agent') || '',
          ipAddress:
            (request.ip ?? request.headers.get('x-forwarded-for')) || '',
          headers: Object.fromEntries(request.headers.entries()),
          url: request.url,
          method: request.method,
          timestamp: new Date(),
        };

        detector.emit('agent.session.continued', existingSession, context);

        return response;
      }

      // Prepare request context for new detection
      const userAgent = request.headers.get('user-agent');
      const ipAddress = request.ip ?? request.headers.get('x-forwarded-for');

      // Extract path from URL for signature verification
      const url = new URL(request.url);
      const pathWithQuery = url.pathname + url.search;

      const context = {
        ...(userAgent && { userAgent }),
        ...(ipAddress && { ipAddress }),
        headers: Object.fromEntries(request.headers.entries()),
        url: pathWithQuery, // Use path instead of full URL for signature verification
        method: request.method,
        timestamp: new Date(),
      };

      // Analyze request
      const result = await detector.analyze(context);

      // Handle detection result
      if (
        result.isAgent &&
        result.confidence >= (config.confidenceThreshold ?? 0.7)
      ) {
        // Call custom detection handler if provided
        if (onDetection) {
          const customResponse = await onDetection(request, result);
          if (customResponse) {
            return customResponse;
          }
        }

        // Handle based on configuration
        switch (onAgentDetected) {
          case 'block': {
            const response = NextResponse.json(
              {
                error: blockedResponse.message,
                detected: true,
                confidence: result.confidence,
                timestamp: result.timestamp,
              },
              { status: blockedResponse.status }
            );

            if (blockedResponse.headers) {
              Object.entries(blockedResponse.headers).forEach(
                ([key, value]) => {
                  response.headers.set(key, value);
                }
              );
            }

            // Emit blocked event
            detector.emit('agent.blocked', result, context);

            return response;
          }

          case 'redirect':
            return NextResponse.redirect(new URL(redirectUrl, request.url));

          case 'rewrite':
            return NextResponse.rewrite(new URL(rewriteUrl, request.url));

          case 'log':
            console.warn('AgentShield: Agent detected', {
              ipAddress: context.ipAddress,
              userAgent: context.userAgent,
              confidence: result.confidence,
              reasons: result.reasons,
              pathname: request.nextUrl.pathname,
            });
            break;

          case 'allow':
          default:
            // Emit allowed event for high-confidence agents
            if (
              result.isAgent &&
              result.confidence >= (config.confidenceThreshold ?? 0.7)
            ) {
              detector.emit('agent.allowed', result, context);
            }
            // Continue processing
            break;
        }
      }

      // Add detection result to request for API routes
      (request as any).agentShield = {
        result,
        skipped: false,
      };

      // Add detection result to response headers for debugging
      let response = NextResponse.next();
      response.headers.set('x-agentshield-detected', result.isAgent.toString());
      response.headers.set(
        'x-agentshield-confidence',
        result.confidence.toString()
      );

      // Add agent name if detected
      if (result.detectedAgent?.name) {
        response.headers.set('x-agentshield-agent', result.detectedAgent.name);
      }

      // Track new AI agent sessions
      if (
        sessionTracker &&
        result.isAgent &&
        result.confidence >= (config.confidenceThreshold ?? 0.7)
      ) {
        response = await sessionTracker.track(request, response, result);
        response.headers.set('x-agentshield-session', 'new');

        // Emit new session event
        detector.emit('agent.session.started', result, context);
      }

      return response;
    } catch (error) {
      console.error('AgentShield middleware error:', error);
      return NextResponse.next(); // Continue on error
    }
  };
}

/**
 * Convenience function for basic setup
 */
export function agentShield(config: Partial<NextJSMiddlewareConfig> = {}) {
  return createAgentShieldMiddleware(config);
}
